import { CI_VERDICT_OPTIONS, ADVANCED_CI_FILTERS, sanitizeCiSearchFilters } from '../../../_helpers/ci-search-filters';
import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
  HostListener,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';

import { CommonModule } from '@angular/common';

import { SharingdataService } from '../../../_services/sharingdata.service';
import { PropertyService } from '../../../_services/properties/property.service';

import {
  ActivatedRoute,
  Router,
  RouterLink,
} from '@angular/router';


import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  tap,
} from 'rxjs';


@Component({
  selector: 'app-search-filters',

  standalone: true,

  imports: [
    ReactiveFormsModule,
    CommonModule,
    RouterLink,
  ],

  templateUrl: './search-filters.component.html',

  styleUrl: './search-filters.component.scss',
})
export class SearchFiltersComponent implements OnInit, OnDestroy {

  @Input() filterData: any;
  @Input() ciPaidAccess = false;
  readonly ciVerdictOptions = CI_VERDICT_OPTIONS;
  readonly advancedCiOptions = ADVANCED_CI_FILTERS;

  ngOnChanges(): void {
    if (this.form && !this.ciPaidAccess) {
      this.form.patchValue({ advancedCi: [] }, { emitEvent: false });
      this.appliedFilters.advancedCi = [];
    }
  }

  ciFilterLabel(value: string): string {
    return [...this.ciVerdictOptions, ...this.advancedCiOptions].find(option => option.value === value)?.label ?? value;
  }


  @Output() filterOpenChange = new EventEmitter<boolean>();

  @HostListener('document:keydown.escape')
  onEscape(): void { if (this.isFilterOpen) this.cancelFilters(); }

  @Output() filtersApplied =
    new EventEmitter<boolean>();

  @Output() onClose =
    new EventEmitter<void>();

  @Output() filterClicked =
    new EventEmitter<void>();


  form!: FormGroup;


  /* ============================================================
     SEARCH STATE
  ============================================================ */

  activeFilter = 'propertyType';
  readonly filterCategories = [
    { key: 'propertyType', label: 'Property type' },
    { key: 'unitTypes', label: 'Unit type' },
    { key: 'tagType', label: 'Tags' },
    { key: 'budget', label: 'Budget' },
    { key: 'bhk', label: 'BHK' },
    { key: 'status', label: 'Status' },
    { key: 'ciVerdict', label: 'CI verdict' },
    { key: 'advancedCi', label: 'Advanced CI' },
  ];

  categoryCount(key: string): number {
    if (key === 'budget') return this.form.value.minBudget !== 1 || this.form.value.maxBudget !== 100 ? 1 : 0;
    return this.form.value[key]?.length || 0;
  }

  onCategoryKeydown(event: KeyboardEvent, index: number): void {
    let next = index;
    if (event.key === 'ArrowDown') next = (index + 1) % this.filterCategories.length;
    else if (event.key === 'ArrowUp') next = (index - 1 + this.filterCategories.length) % this.filterCategories.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.filterCategories.length - 1;
    else return;
    event.preventDefault();
    this.activeFilter = this.filterCategories[next].key;
    const buttons = (event.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLButtonElement>('button');
    buttons?.[next]?.focus();
  }

  isFilterOpen = false;

  filteredLocations: any[] = [];

  showLocationDropdown = false;
  private suggestionsEnabled = false;

  selectedLocations: string[] = [];

  allProperties: any[] = [];

  allLocations: any[] = [];


  getTagName(value: string): string {
    const tag = this.tagType.find(
      (item: any) => item.value === value
    );

    return tag?.name || value;
  }


  /* ============================================================
     APPLIED FILTERS
  ============================================================ */

  appliedFilters: any = {

    ciVerdict: [],
    advancedCi: [],
    propertyType: [],

    unitTypes: [],

    tagType: [],

    bhk: [],

    status: [],

    minBudget: 1,

    maxBudget: 100,
  };


  /* ============================================================
     FILTER OPTIONS
  ============================================================ */

  propertyTypes = [
    'Apartment',
    'Villa',
    'Penthouse',
    'Plot',
    'Villament',
    'Farmhouse',
    'Boutique Apartment',
  ];


  unitTypes = [
    'Simplex',
    'Duplex',
    'Triplex',
    'Penthouse',
    'Studio Apartment',
  ];


  bhkTypes = [
    '1 BHK',
    '2 BHK',
    '3 BHK',
    '4 BHK',
    '5+ BHK',
  ];


  statusTypes = [
    'Ready to Move',
    'Under Construction',
    'New Launch',
  ];


  tagType: any[] = [
    {
      name: 'Exclusive',
      value: 'isExclusive',
    },
    {
      name: 'High Demand',
      value: 'isHighDemand',
    },
    {
      name: 'Investment Grade',
      value: 'isInvestmentGrade',
    },
    {
      name: 'Legal Verified',
      value: 'isPropertyVerified',
    },
  ];


  private destroy$ =
    new Subject<void>();


  constructor(
    private fb: FormBuilder,

    private _SD: SharingdataService,

    private readonly _PS: PropertyService,

    private router: Router,

    private route: ActivatedRoute,

  ) {}


  /* ============================================================
     INIT
  ============================================================ */

  ngOnInit(): void {

    this.form =
      this.fb.group({

        location: [''],

        ciVerdict: [[]],
        advancedCi: [[]],
        propertyType: [[]],

        unitTypes: [[]],

        tagType: [[]],

        bhk: [[]],

        status: [[]],

        minBudget: [1],

        maxBudget: [100],

      });


    /*
     * Load data required for autocomplete.
     */
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.plotSelected && this.form.value.advancedCi.includes('udsSupportAbove')) {
        this.form.patchValue({ advancedCi: this.form.value.advancedCi.filter((value: string) => value !== 'udsSupportAbove') }, { emitEvent: false });
      }
    });
    this.getClusters();

    this.getProperties();


    /*
     * Hydrate from URL / parent.
     */
    if (this.filterData) {

      const hasValue =
        Object.values(this.filterData)
          .some((val: any) =>
            Array.isArray(val)
              ? val.length > 0
              : val !== null &&
                val !== undefined
          );


      if (hasValue) {

        this.setFormFromQueryParams(
          this.filterData,
          false
        );

      }

    }


    /*
     * Property/location search input.
     */
    this.form
      .get('location')
      ?.valueChanges
      .pipe(
        tap(() => { this.suggestionsEnabled = true; }),
        debounceTime(300),

        distinctUntilChanged(),

        takeUntil(this.destroy$),

      )
      .subscribe((value: string) => {

        this.updateSearchSuggestions(value);

      });

  }


  /* ============================================================
     LOAD ALL PROPERTIES
  ============================================================ */

  private getProperties(): void {

    this._PS
      .getAllProperties(1, 2000)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({

        next: (res: any) => {

          /*
           * Different API responses are handled safely.
           */
          if (Array.isArray(res)) {

            this.allProperties = res;

          } else if (Array.isArray(res?.data)) {

            this.allProperties = res.data;

          } else {

            this.allProperties = [];

          }


          /*
           * If the user has already typed something
           * while the API was loading, refresh suggestions.
           */
          const currentValue =
            this.form?.get('location')?.value;

          if (currentValue) {

            this.updateSearchSuggestions(
              currentValue
            );

          }

        },

        error: (error) => {

          console.error(
            'Unable to load properties for search suggestions:',
            error
          );

          this.allProperties = [];

        },

      });

  }


  /* ============================================================
     LOAD CLUSTERS
  ============================================================ */

  private getClusters(): void {

    this._PS
      .getAllCluster()
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({

        next: (res: any) => {

          this.allLocations =
            Array.isArray(res)
              ? res
              : res?.data || [];

        },

        error: (error) => {

          console.error(
            'Unable to load locations:',
            error
          );

          this.allLocations = [];

        },

      });

  }


  /* ============================================================
     SEARCH SUGGESTIONS
  ============================================================ */

  private updateSearchSuggestions(
    value: string
  ): void {
    if (!this.suggestionsEnabled) return;

    const search =
      (value || '')
        .trim()
        .toLowerCase();


    if (!search) {

      this.filteredLocations = [];

      this.showLocationDropdown = false;

      return;

    }


    /* ----------------------------------------------------------
       LOCATION MATCHES
    ---------------------------------------------------------- */

    const locationMatches =
      this.allLocations

        .filter((loc: any) => {

          const name =
            loc?.micromarketName
              ?.toString()
              .toLowerCase();

          return (
            !!name &&
            name.includes(search) &&
            !this.selectedLocations.includes(
              loc.micromarketName
            )
          );

        })

        .map((loc: any) => ({

          type: 'location',

          label: loc.micromarketName,

        }));


    const uniqueLocationMatches =
      locationMatches.filter(
        (
          item: any,
          index: number,
          self: any[]
        ) =>
          index ===
          self.findIndex(
            (t) =>
              t.label === item.label
          )
      );


    /* ----------------------------------------------------------
       PROPERTY NAME MATCHES
    ---------------------------------------------------------- */

    const propertyMatches =
      this.allProperties

        .filter((property: any) => {

          const name =
            property?.name
              ?.toString()
              .toLowerCase();

          return (
            !!name &&
            name.includes(search)
          );

        })

        .map((property: any) => ({

          type: 'property',

          label: property.name,

          data: property,

        }));


    /* ----------------------------------------------------------
       REMOVE DUPLICATES
    ---------------------------------------------------------- */

    const combined = [
      ...uniqueLocationMatches,
      ...propertyMatches,
    ];


    const seen =
      new Set<string>();


    this.filteredLocations =
      combined.filter((item: any) => {

        const key =
          `${item.type}-${item.label}`
            .toLowerCase();

        if (seen.has(key)) {

          return false;

        }

        seen.add(key);

        return true;

      });


    this.showLocationDropdown =
      this.filteredLocations.length > 0;

  }


  /* ============================================================
     ENTER SEARCH
  ============================================================ */

  searchByText(): void {

    const rawValue =
      this.form
        ?.get('location')
        ?.value;


    const value =
      (rawValue || '')
        .trim();


    if (!value) {

      return;

    }


    /*
     * If an exact property suggestion exists,
     * use that property.
     */
    const exactProperty =
      this.filteredLocations.find(
        (item: any) =>
          item.type === 'property' &&
          item.label
            ?.toLowerCase() ===
          value.toLowerCase()
      );


    if (exactProperty) {

      this.selectSearchItem(
        exactProperty
      );

      return;

    }


    /*
     * Otherwise treat the typed value
     * directly as propertyName.
     */
    const searchData =
      this.buildSearchPayload({

        propertyName: value,

      });


    this.executeSearch(
      searchData
    );

  }


  /* ============================================================
     PROPERTY / LOCATION SELECTION
  ============================================================ */

  selectSearchItem(item: any): void {

    if (!item) {

      return;

    }


    if (item.type === 'location') {

      this.selectLocation(
        item.label
      );

      return;

    }


    if (item.type === 'property') {

      const propertyName =
        item?.data?.name ||
        item?.label;


      const searchData =
        this.buildSearchPayload({

          propertyName,

        });


      /*
       * Keep the selected property name
       * visible in the search field.
       */
      this.form.patchValue(
        {
          location: propertyName,
        },
        {
          emitEvent: false,
        }
      );


      this.showLocationDropdown = false;

      this.filteredLocations = [];


      this.executeSearch(
        searchData
      );

    }

  }


  /* ============================================================
     BUILD SEARCH PAYLOAD
  ============================================================ */

  private buildSearchPayload(
    extra: any = {}
  ): any {

    const payload: any = {

      ...this.appliedFilters,

      location: [
        ...this.selectedLocations,
      ],

      ...extra,

    };


    /*
     * Remove empty arrays.
     */
    Object.keys(payload).forEach(
      (key) => {

        const value =
          payload[key];

        if (
          Array.isArray(value) &&
          value.length === 0
        ) {

          delete payload[key];

        }

      }
    );


    /*
     * Do not send default budget
     * values unnecessarily.
     */
    if (
      payload.minBudget === 1
    ) {

      delete payload.minBudget;

    }

    if (
      payload.maxBudget === 100
    ) {

      delete payload.maxBudget;

    }


    const advanced = this.ciPaidAccess ? payload.advancedCi || [] : [];
    delete payload.advancedCi;
    for (const option of this.advancedCiOptions) {
      if (advanced.includes(option.value)) payload[option.value] = option.value === 'rentalYieldAbove' ? 3 : option.value === 'udsSupportAbove' ? 70 : true;
    }
    return sanitizeCiSearchFilters(payload, this.ciPaidAccess);

  }


  /* ============================================================
     EXECUTE SEARCH
  ============================================================ */

  private executeSearch(
    searchData: any
  ): void {
    this.suggestionsEnabled = false;
    this.showLocationDropdown = false;
    this.filteredLocations = [];

    console.log(
      'CircledPlus search payload:',
      searchData
    );


    /*
     * Update shared state.
     *
     * SearchComponent listens to this and calls
     * searchProperty().
     */
    this._SD.updateResults(
      searchData
    );


    /*
     * Update URL as well.
     */
    this.searchItem(
      searchData
    );


    this.filtersApplied.emit(
      true
    );

  }


  /* ============================================================
     LOCATION FILTER
  ============================================================ */

  selectLocation(
    location: string
  ): void {

    if (
      this.selectedLocations.length >= 3 ||
      this.selectedLocations.includes(location)
    ) {

      return;

    }


    this.selectedLocations.push(
      location
    );


    this.form.patchValue(
      {
        location: '',
      },
      {
        emitEvent: false,
      }
    );


    this.filteredLocations = [];

    this.showLocationDropdown = false;


    const searchData =
      this.buildSearchPayload();


    this.executeSearch(
      searchData
    );

  }


  /* ============================================================
     URL / FORM HYDRATION
  ============================================================ */

  setFormFromQueryParams(
    params: any,
    notify: boolean = true
  ): void {

    params = sanitizeCiSearchFilters(params, this.ciPaidAccess);
    const types = Array.isArray(params.propertyType) ? params.propertyType : [params.propertyType];
    const hasPlot = types.some((type: unknown) => /^plots?$/i.test(String(type ?? '').trim()));
    const advancedCi = this.advancedCiOptions.filter(option => params[option.value] != null && !(hasPlot && option.value === 'udsSupportAbove')).map(option => option.value);
    const patch: any = { ciVerdict: params.ciVerdict || [], advancedCi };


    if (params?.propertyType?.length) {

      patch.propertyType =
        params.propertyType;

    }


    if (params?.propertyName?.length) {

      patch.location =
        Array.isArray(
          params.propertyName
        )
          ? params.propertyName[0]
          : params.propertyName;

    }


    if (params?.unitTypes?.length) {

      patch.unitTypes =
        params.unitTypes;

    }


    if (params?.location?.length) {

      this.selectedLocations = Array.isArray(params.location)
        ? [...params.location]
        : [params.location];

    }


    if (params?.tagType?.length) {
      patch.tagType = params.tagType;
    }

    if (params?.bhk?.length) {

      patch.bhk =
        params.bhk;

    }


    if (params?.status?.length) {

      patch.status =
        params.status;

    }


    if (
      params?.minBudget !== null &&
      params?.minBudget !== undefined
    ) {

      patch.minBudget =
        params.minBudget;

    }


    if (
      params?.maxBudget !== null &&
      params?.maxBudget !== undefined
    ) {

      patch.maxBudget =
        params.maxBudget;

    }


    if (
      Object.keys(patch).length
    ) {

      this.form.patchValue(
        patch,
        {
          emitEvent: false,
        }
      );

    }


    this.appliedFilters = {
      ciVerdict: params.ciVerdict || [],
      advancedCi,
      propertyType:
        params?.propertyType || [],

      unitTypes:
        params?.unitTypes || [],

      tagType:
        params?.tagType || [],

      bhk:
        params?.bhk || [],

      status:
        params?.status || [],

      minBudget:
        params?.minBudget ?? 1,

      maxBudget:
        params?.maxBudget ?? 100,

    };


    if (notify) {

      this.searchItem(
        params
      );

      this._SD.updateResults(
        params
      );

    }

  }


  /* ============================================================
     FILTER MODAL
  ============================================================ */

  toggleFilters(): void {

    this.isFilterOpen =
      !this.isFilterOpen;

    this.filterOpenChange.emit(this.isFilterOpen);
    this.filterClicked.emit();

  }


  closeFilters(): void {
    this.cancelFilters();
  }


  cancelFilters(): void {

    this.form.patchValue({
      ciVerdict: [...(this.appliedFilters.ciVerdict || [])],
      advancedCi: this.ciPaidAccess ? [...(this.appliedFilters.advancedCi || [])] : [],
      propertyType:
        [
          ...(this.appliedFilters.propertyType || [])
        ],

      unitTypes:
        [
          ...(this.appliedFilters.unitTypes || [])
        ],

      tagType:
        [
          ...(this.appliedFilters.tagType || [])
        ],

      bhk:
        [
          ...(this.appliedFilters.bhk || [])
        ],

      status:
        [
          ...(this.appliedFilters.status || [])
        ],

      minBudget:
        this.appliedFilters.minBudget ?? 1,

      maxBudget:
        this.appliedFilters.maxBudget ?? 100,

    });


    this.isFilterOpen = false;
    this.filterOpenChange.emit(false);

  }


  applyFilters(): void {

    this.appliedFilters = {
      ciVerdict: [...(this.form.value.ciVerdict || [])],
      advancedCi: this.ciPaidAccess ? [...(this.form.value.advancedCi || [])] : [],

      propertyType:
        [
          ...(this.form.value.propertyType || [])
        ],

      unitTypes:
        [
          ...(this.form.value.unitTypes || [])
        ],

      tagType:
        [
          ...(this.form.value.tagType || [])
        ],

      bhk:
        [
          ...(this.form.value.bhk || [])
        ],

      status:
        [
          ...(this.form.value.status || [])
        ],

      minBudget:
        this.form.value.minBudget,

      maxBudget:
        this.form.value.maxBudget,

    };


    this.isFilterOpen = false;
    this.filterOpenChange.emit(false);


    const currentPropertyName =
      this.form.value.location
        ?.trim();


    const searchData =
      this.buildSearchPayload(
        currentPropertyName
          ? {
              propertyName:
                currentPropertyName,
            }
          : {}
      );


    this.executeSearch(
      searchData
    );

  }


  /* ============================================================
     FILTER VALUE
  ============================================================ */

  onBudgetInput(field: 'minBudget' | 'maxBudget', event: Event): void {
    const raw = Number((event.target as HTMLInputElement).value);
    const value = Math.max(1, Math.min(100, Number.isFinite(raw) ? raw : 1));
    const other = Number(this.form.get(field === 'minBudget' ? 'maxBudget' : 'minBudget')?.value);
    const bounded = field === 'minBudget' ? Math.min(value, other) : Math.max(value, other);
    this.form.patchValue({ [field]: bounded });
    (event.target as HTMLInputElement).value = String(bounded);
  }

  get plotSelected(): boolean {
    return (this.form?.value.propertyType || []).some((type: string) => /^plots?$/i.test(type.trim()));
  }

  get draftFilterChips(): { field: string; value: string; label: string }[] {
    const draft = this.form?.value || {};
    const chips = this.selectedLocations.map(value => ({ field: 'location', value, label: value }));
    for (const field of ['propertyType', 'unitTypes', 'tagType', 'bhk', 'status', 'ciVerdict', 'advancedCi']) {
      for (const value of draft[field] || []) {
        if (field === 'advancedCi' && (!this.ciPaidAccess || (this.plotSelected && value === 'udsSupportAbove'))) continue;
        const label = field === 'ciVerdict' || field === 'advancedCi' ? this.ciFilterLabel(value)
          : field === 'tagType' ? this.tagType.find(tag => tag.value === value)?.label || value : value;
        chips.push({ field, value, label });
      }
    }
    if (draft.minBudget !== 1 || draft.maxBudget !== 100) chips.push({ field: 'budget', value: '', label: `₹ ${draft.minBudget} – ${draft.maxBudget} Cr${draft.maxBudget === 100 ? '+' : ''}` });
    return chips;
  }

  removeDraftChip(chip: { field: string; value: string }): void {
    if (chip.field === 'budget') {
      this.form.patchValue({ minBudget: 1, maxBudget: 100 });
    } else if (chip.field === 'location') {
      this.selectedLocations = this.selectedLocations.filter(value => value !== chip.value);
    } else {
      this.form.patchValue({ [chip.field]: (this.form.get(chip.field)?.value || []).filter((value: string) => value !== chip.value) });
    }
  }

  toggleValue(
    controlName: string,
    value: string
  ): void {

    if (controlName === 'advancedCi' && !this.ciPaidAccess) return;
    if (controlName === 'advancedCi' && value === 'udsSupportAbove' && this.plotSelected) return;
    const current =
      this.form.get(
        controlName
      )?.value || [];


    if (current.includes(value)) {

      this.form.patchValue({

        [controlName]:
          current.filter(
            (v: string) =>
              v !== value
          ),

      });

    } else {

      this.form.patchValue({

        [controlName]:
          [
            ...current,
            value,
          ],

      });

    }

  }


  /* ============================================================
     APPLIED FILTERS
  ============================================================ */

  get appliedFilterCount(): number {
    const fields = ['propertyType', 'unitTypes', 'bhk', 'status', 'tagType', 'ciVerdict', 'advancedCi'];
    return this.selectedLocations.length + fields.reduce((count, key) => count + (this.appliedFilters[key]?.length || 0), 0)
      + ((this.appliedFilters.minBudget !== 1 || this.appliedFilters.maxBudget !== 100) ? 1 : 0);
  }

  hasAppliedFilters(): boolean {

    const {
      propertyType = [],
      unitTypes = [],
      bhk = [],
      status = [],
      minBudget = 1,
      maxBudget = 100,
    } = this.appliedFilters || {};


    return (

      this.selectedLocations.length > 0 ||

      (this.appliedFilters.ciVerdict?.length || 0) > 0 ||
      (this.ciPaidAccess && (this.appliedFilters.advancedCi?.length || 0) > 0) ||
      propertyType.length > 0 ||

      (this.appliedFilters.tagType?.length || 0) > 0 ||
      unitTypes.length > 0 ||

      bhk.length > 0 ||

      status.length > 0 ||

      minBudget !== 1 ||

      maxBudget !== 100

    );

  }


  removeAppliedLocation(
    index: number
  ): void {

    this.selectedLocations.splice(
      index,
      1
    );


    const searchData =
      this.buildSearchPayload();


    this.executeSearch(
      searchData
    );

  }


  removeAppliedChip(
    type: string,
    value: string
  ): void {

    const current =
      this.form.get(type)
        ?.value || [];


    const updated =
      current.filter(
        (v: string) =>
          v !== value
      );


    this.form.patchValue({

      [type]:
        updated,

    });


    this.appliedFilters[type] =
      [
        ...updated
      ];


    const searchData =
      this.buildSearchPayload();


    this.executeSearch(
      searchData
    );

  }


  removeBudgetFilter(): void {

    this.form.patchValue({

      minBudget: 1,

      maxBudget: 100,

    });


    this.appliedFilters.minBudget = 1;

    this.appliedFilters.maxBudget = 100;


    const searchData =
      this.buildSearchPayload();


    this.executeSearch(
      searchData
    );

  }


  /* ============================================================
     CLEAR
  ============================================================ */

  clearAppliedFilters(): void {

    this.selectedLocations = [];


    this.appliedFilters = {

      ciVerdict: [],
      advancedCi: [],
      propertyType: [],

      unitTypes: [],

      tagType: [],

      bhk: [],

      status: [],

      minBudget: 1,

      maxBudget: 100,

    };


    this.form.reset({

      location: '',

      ciVerdict: [],
      advancedCi: [],
      propertyType: [],

      unitTypes: [],

      tagType: [],

      bhk: [],

      status: [],

      minBudget: 1,

      maxBudget: 100,

    });


    this.filteredLocations = [];

    this.showLocationDropdown = false;


    this._SD.updateResults([]);


    this.clearQueryParams();


    this.filtersApplied.emit(
      false
    );

  }


  clearAll(): void {

    this.form.reset({

      location: '',

      ciVerdict: [],
      advancedCi: [],
      propertyType: [],

      unitTypes: [],

      tagType: [],

      bhk: [],

      status: [],

      minBudget: 1,

      maxBudget: 100,

    });


    this.selectedLocations = [];

    this.filteredLocations = [];

    this.showLocationDropdown = false;

  }


  /* ============================================================
     ROUTING
  ============================================================ */

  searchItem(
    data?: any
  ): void {

    this.router.navigate(
      ['/search'],
      {
        queryParams:
          data || {},
      }
    );

  }


  clearQueryParams(): void {

    this.filterData = null;

    this.router.navigate(['/search'], {
      queryParams: {},
      replaceUrl: true,
    });

  }


  /* ============================================================
     CLEANUP
  ============================================================ */

  ngOnDestroy(): void {

    this.destroy$.next();

    this.destroy$.complete();

  }


  closeMobileView(): void {

    this.onClose.emit();

  }

}
